import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, type TargetKind } from '../../features/discussion/api';

/**
 * Notifications (the bell): the client for notifications.ts, community only.
 * Comments moved to features/discussion/api.ts, which both editions' viewers
 * read; the community pages keep importing them from here.
 */
export { relativeTime, threadSize, toThread, useComments, useCommentMutations, useThread, type CommentRow, type TargetKind } from '../../features/discussion/api';

export interface Notification {
  id: string;
  /** `copy_failed`: a copy the reader asked for, into this collection, could not finish (the preview says why). */
  kind: 'comment' | 'reply' | 'copy_failed';
  actor: string;
  targetKind: TargetKind;
  /** The collection's `col_…` or the study's `stu_…` id. */
  targetId: string;
  collectionId: string | null;
  title: string;
  preview: string;
  createdUtc: string;
  read: boolean;
}

export function useNotifications(enabled: boolean) {
  return useQuery({
    queryKey: ['notifications'],
    queryFn: () => call<{ unread: number; notifications: Notification[] }>('/notifications'),
    enabled,
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { ids?: string[]; all?: boolean }) => call('/notifications/read', { method: 'POST', body: JSON.stringify(v) }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  });
}
